import React from 'react';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import uniq from 'lodash/uniq';
import { Link, Row, Col, BodyText, DropdownMenu } from '@paypalcorp/pp-react';
import { v2blue600, v2grey600, white } from '@paypalcorp/pp-react-colors';
import { Pagination } from '@paypalcorp/pp-react-pagination';

import * as analytics from '../lib/analytics';
import { css } from 'glamor';
import { createHandler } from 'griffin-paypal/number';
import { DataContext } from '../lib/context';
import * as fundraiser from '../lib/fundraiser';

const paginationStyle = css({
  transform: 'translateY(50%)',
  '@media(max-width: 768px)': {
    marginLeft: '4px',
    transform: 'translateY(25%)',
  },
});

const pageStyle = css({
  color: v2blue600,
  margin: '0 11px',
  ':hover': {
    color: v2blue600,
  },
  '@media(max-width: 500px)': {
    color: v2blue600,
    margin: '0 7px',
    ':hover': {
      color: v2blue600,
    },
  },
});

const activePageStyle = css({
  backgroundColor: v2blue600 + '!important',
  transition: 'none',
  ':hover': {
    color: white,
  },
});

const pageArrowSpaceStyle = css({
  width: '0',
});

const pageArrow = css({
  ':hover': {
    color: v2blue600,
  },
  '@media(max-width: 500px)': {
    margin: '0 0',
    padding: '4px 0px',
  },
});

const totalResultsTextStyle = {
  color: v2grey600,
};

export default class extends React.Component {
  static contextType = DataContext;

  static propTypes = {
    query: PropTypes.object,
    campaigns: PropTypes.array,
    totalItems: PropTypes.number,
    states: PropTypes.object,
    searchUrl: PropTypes.string,
    charityUrl: PropTypes.string,
    contentCountry: PropTypes.string,
    isMobile: PropTypes.bool,
  };

  state = {
    page: this.props.query.page ? Number(this.props.query.page) : 1,
    campaigns: this.props.campaigns || [],
    totalPages: Number(this.props.totalCampaignsPages),
    sortBy: this.props.query.sortBy ? this.props.query.sortBy : 'bestmatch',
    pageSize: this.props.query.pageSize
      ? Number(this.props.query.pageSize)
      : 24,
  };

  analyticsData = {
    e: 'cl',
    product: 'dw_giving',
    account_type: analytics.getAccountType(this.props.authState),
    event_source: analytics.getEventSource(this.props.isMobile),
    acct_cntry: this.props.contentCountry,
    filter_type: this.props.query.type ? this.props.query.type : '',
    state: this.props.query.state ? this.props.query.state : '',
    category_code: this.props.query.category ? this.props.query.category : '',
  };

  recordShowMoreEvent = () => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_MORE_FUNDRAISERS_PRESSED,
      this.analyticsData
    );
  };

  recordSortPressedEvent = () => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_SORT_DROPDOWN_PRESSED,
      this.analyticsData
    );
  };

  recordSortSelectionEvent = (optSel) => {
    let optionSelected = 'best_match';
    if (optSel === 'name') {
      optionSelected = 'alphabetical';
    }

    if (optSel === 'trending') {
      optionSelected = 'popular';
    }

    if (optSel === 'bestmatch') {
      optionSelected = 'best_match';
    }

    if (optSel === 'endingFirst') {
      optionSelected = 'ending_first';
    }

    if (optSel === 'closestToGoal') {
      optionSelected = 'closest_to_goal';
    }

    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_SORT_SELECTION_PRESSED,
      Object.assign({}, this.analyticsData, {
        opt_sel: optionSelected,
      })
    );
  };

  recordPageSizePressedEvent = () => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_PAGE_SIZE_DROPDOWN_PRESSED,
      this.analyticsData
    );
  };

  recordPageSizeSelectionEvent = (optSel) => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_PAGE_SIZE_SELECTION_PRESSED,
      Object.assign({}, this.analyticsData, {
        opt_sel: optSel,
      })
    );
  };

  recordPaginationPressedEvent = (optSel) => {
    this.handleLoad(optSel);
    analytics.instance.recordEvent(
      analytics.events.DW_GIVING_BROWSE_FUNDRAISER_CHARITY_PAGINATION_PRESSED,
      Object.assign({}, this.analyticsData, {
        opt_sel: optSel,
      })
    );
  };

  handleLoad = (nextPage) => {
    const urlParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );

    const paramsObj = Object.assign({}, urlParams, {
      page: nextPage,
      pageSize: this.state.pageSize,
      sortBy: this.state.sortBy,
    });

    const params = new URLSearchParams(paramsObj);

    const url = new URL(window.location.origin);
    url.pathname = window.location.pathname;
    url.search = params;
    window.location = url;
  };

  handlePageSize = (e) => {
    this.recordPageSizeSelectionEvent(e.target.value);
    this.setState(
      {
        pageSize: e.target.value,
      },
      () => {
        this.handleLoad(1);
      }
    );
  };

  handleSortBy = (e) => {
    this.recordSortSelectionEvent(e.target.value);
    this.setState(
      {
        sortBy: e.target.value,
      },
      () => {
        this.handleLoad(1);
      }
    );
  };

  formatTotalResults = (campaigns) => {
    if (campaigns.length === 1) {
      return this.props.i18n('label.one-result');
    }

    if (this.props.totalCampaignsItems > this.state.maxResults) {
      return this.props
        .i18n('label.total-fundraiser-results')
        .replace(
          '{totalResults}',
          this.griffinNumberHandler.formatNumber(this.state.maxResults)
        );
    }

    return this.props
      .i18n('label.total-fundraiser-results')
      .replace(
        '{totalResults}',
        this.griffinNumberHandler.formatNumber(this.props.totalCampaignsItems)
      );
  };

  renderList = (campaigns) => {
    const sizePageOptions = [
      { primaryText: '24', value: 24 },
      { primaryText: '48', value: 48 },
      { primaryText: '94', value: 94 },
    ];

    const sortByOptions = [
      {
        primaryText: this.props.i18n('label.sort-by-best-match'),
        value: 'bestmatch',
      },
      {
        primaryText: this.props.i18n('label.sort-by-alphabetical'),
        value: 'name',
      },
      {
        primaryText: this.props.i18n('label.sort-by-popular'),
        value: 'trending',
      },
      {
        primaryText: this.props.i18n('label.sort-by-ending-first'),
        value: 'endingFirst',
      },
      {
        primaryText: this.props.i18n('label.sort-by-closest-to-goal'),
        value: 'closestToGoal',
      },
    ];

    const items = fundraiser.renderItems(campaigns, this.props);

    let totalResults;

    totalResults = (
      <div className={'total-results-container'}>
        <BodyText style={totalResultsTextStyle}>
          {this.formatTotalResults(campaigns)}
        </BodyText>
      </div>
    );

    let sortBy;
    if (this.props.i18n('label.sort-by').length > 1) {
      sortBy = (
        <div className={'sort-by-container'}>
          {totalResults}
          {this.props.searchType === 'fundraisers' && (
            <div className={'select-sort-by'}>
              <DropdownMenu
                label={this.props.i18n('label.sort-by')}
                options={sortByOptions}
                value={this.state.sortBy}
                onClick={this.recordSortPressedEvent}
                onChange={this.handleSortBy}
              />
            </div>
          )}
        </div>
      );
    }

    const selectPageSize = (
      <div className={'select-page-size'}>
        <DropdownMenu
          label={this.props.i18n('label.show')}
          options={sizePageOptions}
          value={this.state.pageSize}
          onClick={this.recordPageSizePressedEvent}
          onChange={this.handlePageSize}
        />
      </div>
    );

    let pagination;
    if (this.state.totalPages > 1) {
      const totalPageCount =
        this.props.totalItems > this.state.maxResults
          ? Math.trunc(this.state.maxResults / this.state.pageSize)
          : this.state.totalPages;
      pagination = (
        <>
          <MediaQuery minWidth={769}>
            <div className='pagination-container'>
              <Pagination
                totalPageCount={totalPageCount}
                currentPage={this.state.page}
                onChange={this.recordPaginationPressedEvent}
                className={`${paginationStyle}`}
                activePageClassName={`${activePageStyle}`}
                pageClassName={`${pageStyle}`}
                arrowSpaceClassName={`${pageArrowSpaceStyle}`}
                arrowLeftClassName={`${pageArrow}`}
                arrowRightClassName={`${pageArrow}`}
              />
              {selectPageSize}
            </div>
          </MediaQuery>
          <MediaQuery maxWidth={768}>
            <div className='pagination-container'>
              <Pagination
                totalPageCount={totalPageCount}
                currentPage={this.state.page}
                onChange={this.recordPaginationPressedEvent}
                className={`${paginationStyle}`}
                activePageClassName={`${activePageStyle}`}
                pageClassName={`${pageStyle}`}
                arrowSpaceClassName={`${pageArrowSpaceStyle}`}
                arrowLeftClassName={`${pageArrow}`}
                arrowRightClassName={`${pageArrow}`}
              />
            </div>
          </MediaQuery>
        </>
      );
    }

    const urlParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );

    const paramsObj = Object.assign({}, urlParams, {
      type: 'fundraisers',
      page: 1,
      pageSize: 24,
      sortBy: 'bestmatch',
    });

    const params = new URLSearchParams(paramsObj);

    const url = new URL(window.location.origin);
    url.pathname = window.location.pathname;
    url.search = params;

    const showMore = (
      <Link target={''} href={url} onClick={this.recordShowMoreEvent}>
        {this.props.i18n('label.show-more-fundraisers')}
      </Link>
    );

    return (
      <>
        {sortBy}
        <Row>
          {this.props.searchType === 'fundraisers' ? items : items.slice(0, 9)}
        </Row>
        {this.props.searchType === 'fundraisers' && pagination}
        {items.length > 5 && this.props.searchType === 'all' && showMore}
      </>
    );
  };

  render() {
    if (!this.griffinNumberHandler) {
      this.griffinNumberHandler = createHandler(this.context.griffin.number);
    }

    return (
      <div className={'list'}>{this.renderList(this.state.campaigns)}</div>
    );
  }
}
